import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import MediaPlayer from '../components/MediaPlayer';

const ProductDetail = ({ siteContent }) => {
  const { id } = useParams();
  const product = siteContent?.productsPage?.products?.find((item) => item.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) return <Navigate to="/products" replace />;

  const gallery = [product.image, ...(product.gallery || [])].filter(Boolean);

  return (
    <main className="bg-white pt-24">
      <section className="border-b border-[#e5e5e5] bg-[#f6f7f5] px-6 py-12 md:py-20">
        <div className="mx-auto max-w-7xl">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm font-semibold text-[#5f5f5f] transition-colors hover:text-[#171717]">
            <SafeIcon icon={FiArrowLeft} /> All products
          </Link>
          <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-end">
            <div>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#747474]">Patience AI product</p>
              <h1 className="text-4xl font-medium leading-tight text-[#171717] md:text-6xl">{product.name}</h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#5f5f5f] md:text-xl">{product.shortTagline || product.summary}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {product.demoUrl ? (
                  <a href={product.demoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#171717] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-black">
                    Visit product <SafeIcon icon={FiArrowRight} />
                  </a>
                ) : null}
                <a href="#capabilities" className="inline-flex items-center border border-[#a8a8a8] px-5 py-3 text-sm font-semibold text-[#171717] transition-colors hover:border-[#171717]">
                  Explore capabilities
                </a>
              </div>
            </div>
            <div className="aspect-[16/10] overflow-hidden bg-[#dedede]">
              <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
          <div>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#747474]">Overview</p>
            <h2 className="text-3xl font-medium text-[#171717] md:text-5xl">Built for practical outcomes</h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#555555]">{product.summary}</p>
          </div>
          <dl className="border-l border-[#d5d5d5] pl-6">
            <div className="pb-7">
              <dt className="text-xs font-bold uppercase tracking-[0.16em] text-[#777777]">For</dt>
              <dd className="mt-2 leading-relaxed text-[#252525]">{product.audience}</dd>
            </div>
            <div className="border-t border-[#dedede] pt-7">
              <dt className="text-xs font-bold uppercase tracking-[0.16em] text-[#777777]">Privacy and trust</dt>
              <dd className="mt-2 leading-relaxed text-[#252525]">{product.privacyTone}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section id="capabilities" className="bg-[#171717] px-6 py-16 text-white md:py-24">
        <div className="mx-auto max-w-7xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#b9d2bc]">Capabilities</p>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <h2 className="text-3xl font-medium leading-tight md:text-5xl">What {product.name} delivers</h2>
            <ul className="grid gap-x-10 gap-y-6 md:grid-cols-2">
              {(product.benefits || []).map((benefit) => (
                <li key={benefit} className="flex gap-3 border-t border-white/20 pt-5 text-base leading-relaxed text-white/80">
                  <SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-[#b9d2bc]" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:py-24">
        <div className="mx-auto max-w-7xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#747474]">Platform</p>
          <h2 className="text-3xl font-medium text-[#171717] md:text-5xl">Technology and delivery</h2>
          <div className="mt-10 grid gap-px border border-[#dedede] bg-[#dedede] md:grid-cols-2">
            {(product.technologies || []).map((technology) => (
              <div key={technology} className="bg-white p-6 text-lg leading-relaxed text-[#333333]">{technology}</div>
            ))}
          </div>
        </div>
      </section>

      {product.media?.length ? (
        <section className="border-y border-[#e5e5e5] bg-[#f6f7f5] px-6 py-16 md:py-24">
          <div className="mx-auto max-w-7xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#747474]">Watch</p>
            <h2 className="mb-10 text-3xl font-medium text-[#171717] md:text-5xl">See {product.name} in action</h2>
            <div className={product.media.length > 1 ? 'mx-auto grid max-w-6xl gap-6 md:grid-cols-2' : 'mx-auto max-w-3xl'}>
              {product.media.map((media) => <MediaPlayer key={media.src} {...media} />)}
            </div>
          </div>
        </section>
      ) : null}

      {gallery.length > 1 ? (
        <section className="px-6 py-16 md:py-24">
          <div className="mx-auto max-w-7xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#747474]">Gallery</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {gallery.map((image, index) => (
                <div key={image} className="aspect-[4/3] overflow-hidden bg-[#dedede]">
                  <img src={image} alt={`${product.name} visual ${index + 1}`} loading="lazy" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
};

export default ProductDetail;
